
export default function HOAOutcomes() {
  const metrics = [
    {
      value: '85%',
      label: 'Faster Request Resolution',
      description: 'From 7-14 days to 24-48 hours',
      icon: 'ri-timer-flash-line',
      color: '#1FA372',
      bgColor: '#F0FDF9'
    },
    {
      value: '52%',
      label: 'Higher Payment Completion',
      description: 'Streamlined 3-step payment flow',
      icon: 'ri-money-dollar-circle-line',
      color: '#F97316',
      bgColor: '#FFF7ED'
    },
    {
      value: '45%',
      label: 'Reduced Support Tickets',
      description: 'Self-service and real-time updates',
      icon: 'ri-customer-service-2-line',
      color: '#3B82F6',
      bgColor: '#EFF6FF'
    },
    {
      value: '98%',
      label: 'User Satisfaction Score',
      description: 'Based on post-launch surveys',
      icon: 'ri-emotion-happy-line',
      color: '#8B5CF6',
      bgColor: '#F5F3FF'
    }
  ];

  const improvements = [
    {
      area: 'Onboarding Time',
      before: '45 min',
      after: '8 min',
      improvement: '82% faster'
    },
    {
      area: 'Payment Steps',
      before: '7 steps',
      after: '3 steps',
      improvement: '57% fewer'
    },
    {
      area: 'Request Tracking',
      before: 'Manual calls',
      after: 'Real-time',
      improvement: '100% visibility'
    },
    {
      area: 'Document Access',
      before: '3-5 days',
      after: 'Instant',
      improvement: '99% faster'
    }
  ];

  return (
    <section className="py-24 bg-[#FAFBFC]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-[#1FA372]/10 text-[#1FA372] rounded-full text-sm font-semibold mb-4 border border-[#1FA372]/20">
            09 — Results
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#1E293B] mb-6">
            Outcomes & UX Impact Metrics
          </h2>
          <p className="text-[#64748B] text-lg max-w-3xl mx-auto">
            Measurable results that demonstrate the value of user-centered design in enterprise software.
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {metrics.map((metric, index) => (
            <div 
              key={index}
              className="bg-white rounded-3xl p-6 border border-[#E2E8F0] text-center relative overflow-hidden group hover:shadow-lg transition-all hover:-translate-y-1"
            >
              <div 
                className="absolute top-0 right-0 w-24 h-24 rounded-full blur-2xl opacity-30 group-hover:opacity-50 transition-opacity"
                style={{ backgroundColor: metric.color }}
              ></div>
              
              <div className="relative">
                <div 
                  className="w-14 h-14 flex items-center justify-center rounded-2xl mx-auto mb-4 border"
                  style={{ backgroundColor: metric.bgColor, borderColor: `${metric.color}30` }}
                >
                  <i className={`${metric.icon} text-2xl`} style={{ color: metric.color }}></i>
                </div>
                <div className="text-5xl font-bold mb-2" style={{ color: metric.color }}>
                  {metric.value}
                </div>
                <h3 className="text-[#1E293B] font-semibold mb-2">{metric.label}</h3>
                <p className="text-[#64748B] text-sm">{metric.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Before/After Improvements */}
        <div className="bg-white rounded-3xl p-8 border border-[#E2E8F0] mb-16 shadow-sm">
          <h3 className="text-2xl font-bold text-[#1E293B] mb-8 text-center">UX Improvements Summary</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full min-w-[500px]">
              <thead>
                <tr className="border-b border-[#E2E8F0]">
                  <th className="text-left py-4 px-4 text-[#64748B] font-medium">Area</th>
                  <th className="text-center py-4 px-4 text-[#EF4444] font-medium">Before</th>
                  <th className="text-center py-4 px-4 text-[#1FA372] font-medium">After</th>
                  <th className="text-center py-4 px-4 text-[#1E293B] font-medium">Improvement</th>
                </tr>
              </thead>
              <tbody>
                {improvements.map((item, index) => (
                  <tr key={index} className="border-b border-[#F1F5F9]">
                    <td className="py-4 px-4 text-[#1E293B] font-medium">{item.area}</td>
                    <td className="py-4 px-4 text-center">
                      <span className="px-3 py-1 bg-[#FEF2F2] text-[#EF4444] rounded-full text-sm border border-[#EF4444]/20">
                        {item.before}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="px-3 py-1 bg-[#F0FDF9] text-[#1FA372] rounded-full text-sm border border-[#1FA372]/20">
                        {item.after}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-[#1E293B] font-bold">{item.improvement}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* User Testimonials */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-[#F0FDF9] to-white rounded-3xl p-8 border border-[#1FA372]/20 shadow-sm">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-[#1FA372]/10 rounded-full flex items-center justify-center border border-[#1FA372]/20">
                <i className="ri-user-line text-[#1FA372] text-2xl"></i>
              </div>
              <div>
                <h4 className="text-[#1E293B] font-semibold">Property Manager</h4>
                <p className="text-[#64748B] text-sm">Managing 12 communities</p>
              </div>
            </div>
            <p className="text-[#64748B] italic leading-relaxed">
              "This platform has transformed how I manage communities. What used to take hours now takes minutes. The automated workflows alone have saved me 15+ hours per week."
            </p>
            <div className="flex items-center gap-1 mt-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <i key={star} className="ri-star-fill text-[#F97316]"></i>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#FFF7ED] to-white rounded-3xl p-8 border border-[#F97316]/20 shadow-sm">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-[#F97316]/10 rounded-full flex items-center justify-center border border-[#F97316]/20">
                <i className="ri-home-heart-line text-[#F97316] text-2xl"></i>
              </div>
              <div>
                <h4 className="text-[#1E293B] font-semibold">Homeowner</h4>
                <p className="text-[#64748B] text-sm">Resident for 3 years</p>
              </div>
            </div>
            <p className="text-[#64748B] italic leading-relaxed">
              "Finally, an HOA app that actually works! I can pay my dues, submit requests, and stay updated on community news all in one place. The mobile app is fantastic."
            </p>
            <div className="flex items-center gap-1 mt-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <i key={star} className="ri-star-fill text-[#F97316]"></i>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
